import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Subject,
  LooksOne,
  LooksTwo,
  Looks3,
  Looks4,
  TableChart,
  FormatListNumbered,
  FormatListBulleted,
  TaskAlt,
  FormatQuote,
  // Code as CodeIcon,
  HorizontalRule,
  ArrowRight,
  Image as ImageIcon,
  InsertPageBreak,
  ViewColumn,
  Functions,
  GifBox,
  // Poll,
  CalendarToday,
  Draw,
} from '@mui/icons-material';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
// import { $createCodeNode } from '@lexical/code';
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { INSERT_COLLAPSIBLE_COMMAND } from '../CollapsiblePlugin';
import { INSERT_EXCALIDRAW_COMMAND } from '../ExcalidrawPlugin';
import { INSERT_PAGE_BREAK } from '../PageBreakPlugin';
import useModal from '../../hooks/useModal';
import Modal from '../../ui/Modal';
import {
  InsertImageDialog,
  InsertGIFDialog,
  // InsertPollDialog,
  InsertTableDialog,
  InsertEquationDialog,
  InsertColumnsDialog,
} from '../../ui/InsertDialogs';

const ICON_COLOR = '#4b5563';
const iconSx = { fontSize: 20, color: ICON_COLOR };

export class ComponentPickerOption {
  constructor(title, options) {
    this.key = title;
    this.ref = { current: null };
    this.title = title;
    this.keywords = options.keywords || [];
    this.icon = options.icon;
    this.keyboardShortcut = options.keyboardShortcut;
    this.onSelect = options.onSelect;
  }
}

export function ComponentPickerMenuItem({ index, isSelected, onClick, onMouseEnter, option }) {
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={`item ${isSelected ? 'selected' : ''}`}
      ref={option.ref}
      role="option"
      aria-selected={isSelected}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {option.icon}
      <span className="text">{option.title}</span>
    </li>
  );
}

export function getBaseOptions(editor, showModal, onUploadImage) {
  return [
    new ComponentPickerOption('Paragraph', {
      icon: <Subject sx={iconSx} />,
      keywords: ['normal', 'paragraph', 'p', 'text'],
      onSelect: () =>
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode());
          }
        }),
    }),
    ...([1, 2, 3, 4].map((n) => {
      const HeadingIcon = [LooksOne, LooksTwo, Looks3, Looks4][n - 1];
      return new ComponentPickerOption(`Heading ${n}`, {
        icon: <HeadingIcon sx={iconSx} />,
        keywords: ['heading', 'header', `h${n}`],
        onSelect: () =>
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode(`h${n}`));
            }
          }),
      });
    })),
    new ComponentPickerOption('Table', {
      icon: <TableChart sx={iconSx} />,
      keywords: ['table', 'grid', 'spreadsheet', 'rows', 'columns'],
      onSelect: () =>
        showModal('Insert Table', (onClose) => (
          <InsertTableDialog editor={editor} onClose={onClose} />
        )),
    }),
    new ComponentPickerOption('Numbered List', {
      icon: <FormatListNumbered sx={iconSx} />,
      keywords: ['numbered list', 'ordered list', 'ol'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption('Bulleted List', {
      icon: <FormatListBulleted sx={iconSx} />,
      keywords: ['bulleted list', 'unordered list', 'ul'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption('Check List', {
      icon: <TaskAlt sx={iconSx} />,
      keywords: ['check list', 'todo list'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined),
    }),
    new ComponentPickerOption('Quote', {
      icon: <FormatQuote sx={iconSx} />,
      keywords: ['block quote'],
      onSelect: () =>
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createQuoteNode());
          }
        }),
    }),
    // new ComponentPickerOption('Code Block', {
    //   icon: <CodeIcon sx={iconSx} />,
    //   keywords: ['javascript', 'python', 'js', 'codeblock'],
    //   onSelect: () =>
    //     editor.update(() => {
    //       const selection = $getSelection();
    //       if ($isRangeSelection(selection)) {
    //         if (selection.isCollapsed()) {
    //           $setBlocksType(selection, () => $createCodeNode());
    //         } else {
    //           const textContent = selection.getTextContent();
    //           const codeNode = $createCodeNode();
    //           selection.insertNodes([codeNode]);
    //           const sel2 = $getSelection();
    //           if ($isRangeSelection(sel2)) sel2.insertRawText(textContent);
    //         }
    //       }
    //     }),
    // }),
    new ComponentPickerOption('Divider', {
      icon: <HorizontalRule sx={iconSx} />,
      keywords: ['horizontal rule', 'divider', 'hr'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined),
    }),
    new ComponentPickerOption('Collapsible', {
      icon: <ArrowRight sx={iconSx} />,
      keywords: ['collapse', 'collapsible', 'toggle'],
      onSelect: () => editor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, undefined),
    }),
    new ComponentPickerOption('Image', {
      icon: <ImageIcon sx={iconSx} />,
      keywords: ['image', 'photo', 'picture', 'file'],
      onSelect: () =>
        showModal('Insert Image', (onClose) => (
          <InsertImageDialog editor={editor} onClose={onClose} onUploadImage={onUploadImage} />
        )),
    }),
    new ComponentPickerOption('Page Break', {
      icon: <InsertPageBreak sx={iconSx} />,
      keywords: ['page break', 'page', 'break'],
      onSelect: () => editor.dispatchCommand(INSERT_PAGE_BREAK, undefined),
    }),
    new ComponentPickerOption('Columns Layout', {
      icon: <ViewColumn sx={iconSx} />,
      keywords: ['columns', 'layout', 'grid', 'two columns', 'three columns'],
      onSelect: () =>
        showModal('Insert Columns Layout', (onClose) => (
          <InsertColumnsDialog editor={editor} onClose={onClose} />
        )),
    }),
    new ComponentPickerOption('Excalidraw', {
      icon: <Draw sx={iconSx} />,
      keywords: ['excalidraw', 'diagram', 'drawing', 'sketch'],
      onSelect: () =>
        editor.dispatchCommand(INSERT_EXCALIDRAW_COMMAND, undefined),
    }),
    new ComponentPickerOption('Equation', {
      icon: <Functions sx={iconSx} />,
      keywords: ['equation', 'math', 'latex', 'katex'],
      onSelect: () =>
        showModal('Insert Equation', (onClose) => (
          <InsertEquationDialog editor={editor} onClose={onClose} />
        )),
    }),
    new ComponentPickerOption('GIF', {
      icon: <GifBox sx={iconSx} />,
      keywords: ['gif', 'animate', 'image'],
      onSelect: () =>
        showModal('Insert GIF', (onClose) => (
          <InsertGIFDialog editor={editor} onClose={onClose} />
        )),
    }),
    // new ComponentPickerOption('Poll', {
    //   icon: <Poll sx={iconSx} />,
    //   keywords: ['poll', 'vote', 'survey'],
    //   onSelect: () =>
    //     showModal('Insert Poll', (onClose) => (
    //       <InsertPollDialog editor={editor} onClose={onClose} />
    //     )),
    // }),
    new ComponentPickerOption('Date', {
      icon: <CalendarToday sx={iconSx} />,
      keywords: ['date', 'today', 'calendar', 'time'],
      onSelect: () => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const dateStr = new Date().toLocaleDateString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
            selection.insertText(dateStr);
          }
        });
      },
    }),
  ];
}

export default function ComponentPickerPlugin({ onUploadImage }) {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState(null);
  const [modal, showModal] = useModal();
  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  });

  const options = useMemo(() => {
    const baseOptions = getBaseOptions(editor, showModal, onUploadImage);

    if (!queryString) return baseOptions;

    const regex = new RegExp(queryString, 'i');
    return baseOptions.filter(
      (option) =>
        regex.test(option.title) ||
        option.keywords.some((keyword) => regex.test(keyword))
    );
  }, [editor, queryString, showModal, onUploadImage]);

  const onSelectOption = useCallback(
    (selectedOption, nodeToRemove, closeMenu, matchingString) => {
      editor.update(() => {
        if (nodeToRemove) nodeToRemove.remove();
        selectedOption.onSelect(matchingString);
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <>
      {modal != null && (
        <Modal onClose={modal.onClose} title={modal.title} closeOnClickOutside>
          {modal.content}
        </Modal>
      )}
      <LexicalTypeaheadMenuPlugin
        onQueryChange={setQueryString}
        onSelectOption={onSelectOption}
        triggerFn={checkForTriggerMatch}
        options={options}
        menuRenderFn={(
          anchorElementRef,
          { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
        ) =>
          anchorElementRef.current && options.length
            ? createPortal(
              <div className="typeahead-popover component-picker-menu">
                <ul>
                  {options.map((option, i) => (
                    <ComponentPickerMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </ul>
              </div>,
              anchorElementRef.current
            )
            : null
        }
      />
    </>
  );
}
